import * as React from 'react'

import { BookOpen, File, Question, Video } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { QuizBlockConsumer } from 'modules/trainings/components/content/items/quiz/QuizBlockConsumer'
import { ContentItem } from 'modules/trainings/lib/content/ContentItem'
import { ContentModule } from 'modules/trainings/lib/content/Module'
import { PageBlock } from 'modules/trainings/lib/content/PageBlock'
import { QuizBlock } from 'modules/trainings/lib/content/QuizBlock'
import { VideoBlock } from 'modules/trainings/lib/content/VideoBlock'

import { ModuleEditor } from './items/module/ModuleEditor'
import { ModuleView } from './items/module/ModuleView'
import { PageBlockEditor } from './items/page/PageBlockEditor'
import { PageBlockView } from './items/page/PageBlockView'
import { QuizBlockEditor } from './items/quiz/QuizBlockEditor'
import QuizBlockView from './items/quiz/QuizBlockView'
import { VideoBlockConsumer } from './items/video/VideoBlockConsumer'
import { VideoBlockEditor } from './items/video/VideoBlockEditor'
import { VideoBlockView } from './items/video/VideoBlockView'
import {
  ContentConsumerCallbacks,
  ContentConsumerProps,
  ContentEditorProps,
  ContentViewProps,
} from './types'

// Type alias for F0 icon components
// The design system doesn't export an IconType, so we infer it from an actual icon
type IconType = typeof BookOpen

export type ContentTypeDefinition<T extends ContentItem = ContentItem> = {
  icon: IconType
  label: () => string
  description: () => string
  editorComponent: React.ComponentType<ContentEditorProps<T>>
  viewComponent: React.ComponentType<ContentViewProps<T>>
  consumerComponent: // Block consumer component
  | React.ComponentType<ContentConsumerProps<T>>
    // Block consumer that can interact with progress
    | React.ForwardRefExoticComponent<
        ContentConsumerProps<T> & React.RefAttributes<ContentConsumerCallbacks>
      >
}

class ContentTypeRegistry {
  private definitions = new Map<
    string,
    ContentTypeDefinition<ContentItem> & {
      constructor: new (data?: Record<string, unknown>) => ContentItem
      typeString: string
    }
  >()

  register<T extends ContentItem>(
    typeString: string,
    constructor: new (data?: Record<string, unknown>) => T,
    config: ContentTypeDefinition<T>
  ): void {
    const definition = {
      ...config,
      constructor,
      typeString,
    }
    // Type erasure at runtime - all definitions stored with base ContentItem type
    // @ts-expect-error: Generic type T cannot be safely assigned to base type, but this is safe at runtime
    this.definitions.set(typeString, definition)
  }

  get<T extends ContentItem = ContentItem>(
    type: string
  ):
    | (ContentTypeDefinition<T> & {
        constructor: new (data?: Record<string, unknown>) => T
        typeString: string
      })
    | undefined {
    // Caller is responsible for knowing the correct type
    // @ts-expect-error: Widening base type to generic T, safe when caller knows the correct type
    return this.definitions.get(type)
  }

  getAll(): Array<
    ContentTypeDefinition<ContentItem> & {
      constructor: new (data?: Record<string, unknown>) => ContentItem
      typeString: string
    }
  > {
    return Array.from(this.definitions.values())
  }

  createDefaultItem<T extends ContentItem = ContentItem>(type: string, trainingId?: string): T {
    const definition = this.get<T>(type)
    if (!definition) {
      throw new Error(`No content type registered for type: ${type}`)
    }
    return new definition.constructor({ trainingId })
  }

  /**
   * Generic method to fetch any property from a content type definition.
   * Accepts either a ContentItem instance or a type string.
   *
   * @param itemOrType - Either a ContentItem instance or a type string
   * @param property - The property name to retrieve from the definition
   * @returns The property value, or undefined if not found
   */
  fetch<K extends keyof ContentTypeDefinition>(
    itemOrType: ContentItem | string,
    property: K
  ): ContentTypeDefinition[K] {
    const typeString =
      typeof itemOrType === 'string' ? itemOrType : this.getTypeStringForItem(itemOrType)

    if (!typeString) {
      throw new Error(`No entry for : ${itemOrType}`)
    }

    const definition = this.get(typeString)

    if (!definition) {
      throw new Error(`No entry for : ${typeString}`)
    }

    return definition[property]
  }

  getTypeStringForItem(item: ContentItem): string | undefined {
    for (const [typeString, definition] of this.definitions) {
      if (item instanceof definition.constructor) {
        return typeString
      }
    }
    return undefined
  }
}

export const contentRegistry = new ContentTypeRegistry()

contentRegistry.register('module', ContentModule, {
  icon: BookOpen,
  label: () => i18n.t('trainings.content.content_modal.types.module_label'),
  description: () => i18n.t('trainings.content.content_modal.types.module_description'),
  editorComponent: ModuleEditor,
  viewComponent: ModuleView,
  consumerComponent: ModuleView,
})

contentRegistry.register('quiz', QuizBlock, {
  icon: Question,
  label: () => i18n.t('trainings.content.content_modal.types.quiz_label'),
  description: () => i18n.t('trainings.content.content_modal.types.quiz_description'),
  editorComponent: QuizBlockEditor,
  viewComponent: QuizBlockView,
  consumerComponent: QuizBlockConsumer,
})

contentRegistry.register('page', PageBlock, {
  icon: File,
  label: () => i18n.t('trainings.content.content_modal.types.page_label'),
  description: () => i18n.t('trainings.content.content_modal.types.page_description'),
  editorComponent: PageBlockEditor,
  viewComponent: PageBlockView,
  consumerComponent: PageBlockView,
})

contentRegistry.register('video', VideoBlock, {
  icon: Video,
  label: () => i18n.t('trainings.content.content_modal.types.video_label'),
  description: () => i18n.t('trainings.content.content_modal.types.video_description'),
  editorComponent: VideoBlockEditor,
  viewComponent: VideoBlockView,
  consumerComponent: VideoBlockConsumer,
})
